import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
    Box, Typography, Card, Button, TextField, InputAdornment,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Select, MenuItem, FormControl, InputLabel, Pagination, CircularProgress,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Divider, Grid, Switch, Autocomplete, Chip,
} from "@mui/material";
import {
    Search, AddCircleOutline, ChatOutlined,
    DeleteOutline, Refresh, Close, PersonAdd, WarningAmberRounded, EditOutlined,
    SmartToy, LocalOfferOutlined, FileDownloadOutlined, OpenInNew,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMAvatar from "../common/CRMAvatar";
import CRMBadge from "../common/CRMBadge";
import { isCrmAdmin } from "../common/crmRoles";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";

const BASE_HEADERS = ["Contact", "Phone", "Alt. Phone", "Company", "Source", "Actions"];
const ALT_PHONE_MAX = 255;
const LOCATION_MAX = 255;

const STATUS_OPTIONS = ["", "interested", "not_interested"];
const SOURCE_OPTIONS = ["", "Facebook", "Google", "Manual", "WhatsApp", "Direct Inquiry", "Referral", "IndiaMart", "Justdial", "LinkedIn", "Others"];

const STATUS_LABELS = {
    interested:     "Interested",
    not_interested: "Not Interested",
};

const EXPORT_PAGE_SIZE = 100;

const EMPTY_EXPORT_FILTERS = { search: "", status: "", source: "", tagFilter: "" };

const buildContactsQueryParams = ({ page, perPage, search, status, source, tagFilter }) => {
    const params = { page, per_page: perPage };
    if (search?.trim()) params.search = search.trim();
    if (status) params.status = status;
    if (source) params.source = source;
    if (tagFilter) params.tag_id = Number(tagFilter);
    return params;
};

const formatExcelDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const contactTagsLabel = (contact) => {
    if (Array.isArray(contact?.tags) && contact.tags.length) {
        return contact.tags.map((t) => t.tag_name).filter(Boolean).join(", ");
    }
    return "";
};

const downloadContactsExcel = (rows) => {
    const sheetData = rows.map((row, index) => ({
        "S.No": index + 1,
        Name: row.name || "",
        Phone: row.phone || "",
        "Alternate Phone": row.alternate_contact_number || "",
        Email: row.email || "",
        Company: row.company_name || "",
        Location: row.location || "",
        Source: row.source || "",
        Status: STATUS_LABELS[row.status] || row.status || "",
        Tags: contactTagsLabel(row),
        "Bot Enabled": row.is_bot_enabled === true || row.is_bot_enabled === 1 || row.is_bot_enabled === "1" ? "Yes" : "No",
        "Created At": formatExcelDate(row.created_at),
    }));

    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(sheetData);
    if (sheetData.length > 0) {
        const headers = Object.keys(sheetData[0]);
        sheet["!cols"] = headers.map((key) => {
            const maxLen = Math.max(
                key.length,
                ...sheetData.map((row) => String(row[key] ?? "").length)
            );
            return { wch: Math.min(Math.max(maxLen + 2, 12), 40) };
        });
    }
    XLSX.utils.book_append_sheet(workbook, sheet, "Contacts");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `crm_contacts_${stamp}.xlsx`);
};

const EMPTY_FORM = { phone: "", alternate_contact_number: "", name: "", email: "", company_name: "", location: "", source: "", status: "interested", is_bot_enabled: true, contact_tag_ids: [] };

const TAG_COLOR_PALETTE = [
    { bg: "#fef3c7", text: "#92400e" },
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ffe4e6", text: "#be123c" },
];

const getContactTagIds = (contact) => {
    if (Array.isArray(contact?.tags)) return contact.tags.map((t) => t.id).filter((id) => id != null);
    const raw = contact?.contact_tag_ids;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
    return String(raw).split(",").map((s) => Number(s.trim())).filter(Boolean);
};

const TagsField = ({ availableTags, value, onChange }) => (
    <Autocomplete
        multiple
        options={availableTags}
        getOptionLabel={(o) => o.tag_name || ""}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={availableTags.filter((t) => value.includes(t.id))}
        onChange={(_, newVal) => onChange(newVal.map((t) => t.id))}
        renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.tag_name} size="small" />
            ))
        }
        renderInput={(params) => (
            <TextField
                {...params}
                label="Tags"
                placeholder={availableTags.length ? "Select tags" : "No tags available"}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
        )}
        disabled={availableTags.length === 0}
        noOptionsText="No tags found"
    />
);

// ─── Bot colour theme ─────────────────────────────────────────────────────────
const BOT = {
    color:      "#7c3aed",   // violet-700
    colorLight: "#a78bfa",   // violet-400
    bg:         "#f5f3ff",   // violet-50
    bgDisabled: "#f9fafb",
    border:     "#ddd6fe",   // violet-200
    borderOff:  "#e5e7eb",
    switchOn:   "#7c3aed",
    iconOff:    "#9ca3af",
};

const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
};

// ─── Add Contact Dialog ────────────────────────────────────────────────────────
const AddContactDialog = ({ open, onClose, onCreated, showCrmBotControls, availableTags }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [form, setForm]     = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) { setForm(EMPTY_FORM); setErrors({}); }
    }, [open]);

    const set = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validate = () => {
        const errs = {};
        if (!form.phone.trim()) errs.phone = "Phone number is required";
        const alt = form.alternate_contact_number.trim();
        if (alt.length > ALT_PHONE_MAX) errs.alternate_contact_number = `Max ${ALT_PHONE_MAX} characters`;
        if (form.location.trim().length > LOCATION_MAX) errs.location = `Max ${LOCATION_MAX} characters`;
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const payload = { phone: form.phone.trim(), is_bot_enabled: form.is_bot_enabled };
            if (form.name)         payload.name         = form.name.trim();
            const alt = form.alternate_contact_number.trim();
            if (alt)               payload.alternate_contact_number = alt;
            if (form.email)        payload.email        = form.email.trim();
            if (form.company_name) payload.company_name = form.company_name.trim();
            if (form.location)     payload.location     = form.location.trim();
            if (form.source)       payload.source       = form.source;
            if (form.status)       payload.status       = form.status;
            if (form.contact_tag_ids.length) payload.contact_tag_ids = form.contact_tag_ids;

            await axiosInstance.post(`${base_url}/crm/contacts`, payload);
            notifyRef.current("Contact added successfully", "success");
            onCreated();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to create contact";
            notifyRef.current(msg, "error");
            // Surface field-level errors from API if any
            const apiErrors = err?.response?.data?.errors || {};
            if (Object.keys(apiErrors).length) setErrors(apiErrors);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(17,24,39,0.06)", display: "grid", placeItems: "center" }}>
                        <PersonAdd sx={{ fontSize: 16, color: "text.primary" }} />
                    </Box>
                    Add New Contact
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    {/* Phone — required */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Phone Number"
                            placeholder="e.g. +91 98765 43210"
                            value={form.phone}
                            onChange={set("phone")}
                            size="small"
                            error={!!errors.phone}
                            helperText={errors.phone || "Required — used as unique identifier"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Alternate phone — optional */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Alternate Phone"
                            placeholder="e.g. +91 87654 32109"
                            value={form.alternate_contact_number}
                            onChange={set("alternate_contact_number")}
                            size="small"
                            inputProps={{ maxLength: ALT_PHONE_MAX }}
                            error={!!errors.alternate_contact_number}
                            helperText={errors.alternate_contact_number || "Optional secondary contact number"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Full Name"
                            placeholder="e.g. Rahul Sharma"
                            value={form.name}
                            onChange={set("name")}
                            size="small"
                            error={!!errors.name}
                            helperText={errors.name || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            placeholder="e.g. rahul@techcorp.in"
                            value={form.email}
                            onChange={set("email")}
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Company */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            placeholder="e.g. TechCorp"
                            value={form.company_name}
                            onChange={set("company_name")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Location"
                            placeholder="e.g. Mumbai, Maharashtra"
                            value={form.location}
                            onChange={set("location")}
                            size="small"
                            inputProps={{ maxLength: LOCATION_MAX }}
                            error={!!errors.location}
                            helperText={errors.location || "Optional city or area"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Source */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Lead Source"
                            value={form.source}
                            onChange={set("source")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {SOURCE_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s || "Select source"}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={form.status}
                            onChange={set("status")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {STATUS_OPTIONS.filter(Boolean).map((s) => (
                                <MenuItem key={s} value={s}>{STATUS_LABELS[s] ?? s}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12}>
                        <TagsField
                            availableTags={availableTags}
                            value={form.contact_tag_ids}
                            onChange={(ids) => setForm((f) => ({ ...f, contact_tag_ids: ids }))}
                        />
                    </Grid>

                    {/* Bot toggle — WB-AGENT only */}
                    {showCrmBotControls && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                px: 2, py: 1.25, borderRadius: 2,
                                border: "1px solid", borderColor: form.is_bot_enabled ? BOT.border : BOT.borderOff,
                                backgroundColor: form.is_bot_enabled ? BOT.bg : BOT.bgDisabled,
                                transition: "all 0.2s ease",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <SmartToy sx={{ fontSize: 18, color: form.is_bot_enabled ? BOT.color : BOT.iconOff }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: form.is_bot_enabled ? BOT.color : "text.primary" }}>
                                        Auto Bot Reply
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {form.is_bot_enabled ? "Bot will auto-reply to this contact" : "Bot replies disabled for this contact"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                size="small"
                                checked={form.is_bot_enabled}
                                onChange={(e) => setForm((f) => ({ ...f, is_bot_enabled: e.target.checked }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: BOT.switchOn },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BOT.switchOn },
                                }}
                            />
                        </Box>
                    </Grid>
                    )}
                </Grid>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    disabled={saving || !form.phone.trim()}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? "Adding…" : "Add Contact"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Edit Contact Dialog ───────────────────────────────────────────────────────
export const EditContactDialog = ({ open, contact, onClose, onUpdated, showCrmBotControls, availableTags }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [form, setForm]     = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // Pre-fill form whenever a new contact is passed in
    useEffect(() => {
        if (open && contact) {
            setForm({
                phone:                    contact.phone                    || "",
                alternate_contact_number: contact.alternate_contact_number || "",
                name:                     contact.name                     || "",
                email:                    contact.email                    || "",
                company_name:             contact.company_name             || "",
                location:                 contact.location                 || "",
                source:                   contact.source                   || "",
                status:                   contact.status                   || "interested",
                is_bot_enabled:           contact.is_bot_enabled !== undefined ? Boolean(contact.is_bot_enabled) : true,
                contact_tag_ids:          getContactTagIds(contact),
            });
            setErrors({});
        }
    }, [open, contact]);

    const set = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validate = () => {
        const errs = {};
        if (!form.phone.trim()) errs.phone = "Phone number is required";
        const alt = form.alternate_contact_number.trim();
        if (alt.length > ALT_PHONE_MAX) errs.alternate_contact_number = `Max ${ALT_PHONE_MAX} characters`;
        if (form.location.trim().length > LOCATION_MAX) errs.location = `Max ${LOCATION_MAX} characters`;
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        const cid = contact?.id ?? contact?.contact_id;
        setSaving(true);
        try {
            const payload = {
                phone:                    form.phone.trim(),
                alternate_contact_number: form.alternate_contact_number.trim(),
                name:                     form.name.trim(),
                email:                    form.email.trim(),
                company_name:             form.company_name.trim(),
                location:                 form.location.trim(),
                source:                   form.source,
                status:                   form.status,
                is_bot_enabled:           form.is_bot_enabled,
                contact_tag_ids:          form.contact_tag_ids,
            };
            await axiosInstance.put(`${base_url}/crm/contacts/${cid}`, payload);
            notifyRef.current("Contact updated successfully", "success");
            onUpdated();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to update contact";
            notifyRef.current(msg, "error");
            const apiErrors = err?.response?.data?.errors || {};
            if (Object.keys(apiErrors).length) setErrors(apiErrors);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(59,130,246,0.08)", display: "grid", placeItems: "center" }}>
                        <EditOutlined sx={{ fontSize: 16, color: "#3b82f6" }} />
                    </Box>
                    Edit Contact
                    {contact?.name && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
                            — {contact.name}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    {/* Phone */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Phone Number"
                            value={form.phone}
                            onChange={set("phone")}
                            size="small"
                            error={!!errors.phone}
                            helperText={errors.phone || "Required — used as unique identifier"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Alternate phone */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Alternate Phone"
                            value={form.alternate_contact_number}
                            onChange={set("alternate_contact_number")}
                            size="small"
                            inputProps={{ maxLength: ALT_PHONE_MAX }}
                            error={!!errors.alternate_contact_number}
                            helperText={errors.alternate_contact_number || "Optional secondary contact number"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Full Name"
                            value={form.name}
                            onChange={set("name")}
                            size="small"
                            error={!!errors.name}
                            helperText={errors.name || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Company */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={form.company_name}
                            onChange={set("company_name")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Location"
                            value={form.location}
                            onChange={set("location")}
                            size="small"
                            inputProps={{ maxLength: LOCATION_MAX }}
                            error={!!errors.location}
                            helperText={errors.location || "Optional city or area"}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Source */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Lead Source"
                            value={form.source}
                            onChange={set("source")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {SOURCE_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s || "Select source"}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={form.status}
                            onChange={set("status")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {STATUS_OPTIONS.filter(Boolean).map((s) => (
                                <MenuItem key={s} value={s}>{STATUS_LABELS[s] ?? s}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12}>
                        <TagsField
                            availableTags={availableTags}
                            value={form.contact_tag_ids}
                            onChange={(ids) => setForm((f) => ({ ...f, contact_tag_ids: ids }))}
                        />
                    </Grid>

                    {/* Bot toggle — WB-AGENT only */}
                    {showCrmBotControls && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                px: 2, py: 1.25, borderRadius: 2,
                                border: "1px solid", borderColor: form.is_bot_enabled ? BOT.border : BOT.borderOff,
                                backgroundColor: form.is_bot_enabled ? BOT.bg : BOT.bgDisabled,
                                transition: "all 0.2s ease",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <SmartToy sx={{ fontSize: 18, color: form.is_bot_enabled ? BOT.color : BOT.iconOff }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: form.is_bot_enabled ? BOT.color : "text.primary" }}>
                                        Auto Bot Reply
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {form.is_bot_enabled ? "Bot will auto-reply to this contact" : "Bot replies disabled for this contact"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                size="small"
                                checked={form.is_bot_enabled}
                                onChange={(e) => setForm((f) => ({ ...f, is_bot_enabled: e.target.checked }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: BOT.switchOn },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BOT.switchOn },
                                }}
                            />
                        </Box>
                    </Grid>
                    )}
                </Grid>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    disabled={saving || !form.phone.trim()}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? "Saving…" : "Save Changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────
export const DeleteConfirmDialog = ({ open, contact, onClose, onConfirm, deleting }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
    >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(239,68,68,0.08)", display: "grid", placeItems: "center" }}>
                    <WarningAmberRounded sx={{ fontSize: 18, color: "error.main" }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Delete Contact</Typography>
            </Box>
            <IconButton size="small" onClick={onClose} disabled={deleting}>
                <Close fontSize="small" />
            </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Are you sure you want to permanently delete{" "}
                {contact?.name
                    ? <><strong>{contact.name}</strong> ({contact.phone})</>
                    : <strong>{contact?.phone}</strong>
                }?
            </Typography>
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
                This action cannot be undone.
            </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button size="small" onClick={onClose} disabled={deleting} sx={WB_SX.secondaryButton}>
                Cancel
            </Button>
            <Button
                size="small"
                variant="contained"
                onClick={onConfirm}
                disabled={deleting}
                sx={{ ...WB_SX.primaryButton, backgroundColor: "error.main", "&:hover": { backgroundColor: "error.dark" } }}
            >
                {deleting ? "Deleting…" : "Yes, Delete"}
            </Button>
        </DialogActions>
    </Dialog>
);

// ─── Export Contacts Dialog ────────────────────────────────────────────────────
const ExportContactsDialog = ({
    open,
    filters,
    onFiltersChange,
    availableTags,
    exporting,
    progress,
    onClose,
    onExport,
    onCancelExport,
}) => {
    const set = (field) => (e) => onFiltersChange((f) => ({ ...f, [field]: e.target.value }));
    const activeCount = [filters.search, filters.status, filters.source, filters.tagFilter].filter(Boolean).length;

    return (
        <Dialog
            open={open}
            onClose={exporting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={exporting}
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(17,24,39,0.06)", display: "grid", placeItems: "center" }}>
                        <FileDownloadOutlined sx={{ fontSize: 16, color: "text.primary" }} />
                    </Box>
                    Export contacts
                </Box>
                <IconButton size="small" onClick={onClose} disabled={exporting}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                {exporting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                        <CircularProgress size={28} />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {progress.totalPages > 0
                                    ? `Fetching page ${progress.page} of ${progress.totalPages}`
                                    : "Preparing export…"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Building Excel from matching contacts
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Download an Excel file of CRM contacts. Filters below apply to the export only — they do not change the table.
                                {activeCount > 0 ? ` ${activeCount} filter${activeCount !== 1 ? "s" : ""} selected.` : " Leave filters empty to export all contacts."}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                placeholder="Name, phone, company…"
                                value={filters.search}
                                onChange={set("search")}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Status"
                                value={filters.status}
                                onChange={set("status")}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s || "all"} value={s}>{s ? STATUS_LABELS[s] : "All statuses"}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Source"
                                value={filters.source}
                                onChange={set("source")}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            >
                                {SOURCE_OPTIONS.map((s) => (
                                    <MenuItem key={s || "all"} value={s}>{s || "All sources"}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {availableTags.length > 0 && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Tag"
                                    value={filters.tagFilter}
                                    onChange={set("tagFilter")}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                >
                                    <MenuItem value="">All tags</MenuItem>
                                    {availableTags.map((t) => (
                                        <MenuItem key={t.id} value={String(t.id)}>{t.tag_name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {exporting ? (
                    <Button size="small" color="error" variant="outlined" onClick={onCancelExport} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                ) : (
                    <>
                        <Button size="small" onClick={onClose} sx={WB_SX.secondaryButton}>
                            Close
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<FileDownloadOutlined fontSize="small" />}
                            onClick={onExport}
                            sx={WB_SX.primaryButton}
                        >
                            Download Excel
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Contacts Page ────────────────────────────────────────────────────────
const CRMContacts = () => {
    const navigate = useNavigate();
    const userIsCrmAdmin = useMemo(() => isCrmAdmin(), []);
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;
    const showCrmBotControls = (Cookies.get("userRole") || "").split(",").map((r) => r.trim()).includes("WB-AGENT");
    const HEADERS = showCrmBotControls
        ? ["Contact", "Phone", "Alt. Phone", "Company", "Source", "Bot Reply", "Actions"]
        : BASE_HEADERS;

    const [contacts, setContacts]     = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [total, setTotal]           = useState(0);
    const [page, setPage]             = useState(1);
    const [perPage]                   = useState(15);
    const [search, setSearch]         = useState("");
    const [status, setStatus]         = useState("");
    const [source, setSource]         = useState("");
    const [tagFilter, setTagFilter]   = useState("");
    const [loading, setLoading]             = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editTarget, setEditTarget]       = useState(null);
    const [deleteTarget, setDeleteTarget]   = useState(null);
    const [deleting, setDeleting]           = useState(false);
    const [togglingBot, setTogglingBot]     = useState(null); // contact id being toggled
    const [exportOpen, setExportOpen]       = useState(false);
    const [exportFilters, setExportFilters] = useState(EMPTY_EXPORT_FILTERS);
    const [exporting, setExporting]         = useState(false);
    const [exportProgress, setExportProgress] = useState({ page: 0, totalPages: 0 });
    const exportAbortRef = useRef(null);

    const searchTimer = useRef(null);

    const fetchTags = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`${base_url}/crm/contact-tags`);
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            setAvailableTags(list);
        } catch {
            setAvailableTags([]);
        }
    }, []);

    const fetchContacts = useCallback(async (pg = 1, sq = search, st = status, src = source, tg = tagFilter) => {
        setLoading(true);
        try {
            const params = { page: pg, per_page: perPage };
            if (sq)  params.search = sq;
            if (st)  params.status = st;
            if (src) params.source = src;
            if (tg)  params.tag_id = Number(tg);

            const res      = await axiosInstance.get(`${base_url}/crm/contacts`, { params });
            // Response shape: { success, data: { data: [...contacts], total, current_page, ... } }
            const paginator = res.data?.data ?? {};
            const contacts  = Array.isArray(paginator.data) ? paginator.data : [];
            setContacts(contacts);
            setTotal(paginator.total ?? contacts.length);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to load contacts", "error");
        } finally {
            setLoading(false);
        }
    }, [search, status, source, tagFilter, perPage]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    useEffect(() => {
        if (addDialogOpen || editTarget) fetchTags();
    }, [addDialogOpen, editTarget, fetchTags]);

    useEffect(() => {
        fetchContacts(page, search, status, source, tagFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, status, source, tagFilter]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            fetchContacts(1, val, status, source, tagFilter);
        }, 400);
    };

    const handleSourceChange = (e) => { setSource(e.target.value); setPage(1); };
    const handleTagChange = (e) => { setTagFilter(e.target.value); setPage(1); };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const cid = deleteTarget.id ?? deleteTarget.contact_id;
        setDeleting(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/contacts/${cid}`);
            notifyRef.current("Contact deleted successfully", "success");
            setDeleteTarget(null);
            fetchContacts(page, search, status, source, tagFilter);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to delete contact", "error");
        } finally {
            setDeleting(false);
        }
    };

    const handleContactCreated = () => {
        setPage(1);
        fetchContacts(1, search, status, source, tagFilter);
    };

    const openExportDialog = () => {
        setExportFilters({ search, status, source, tagFilter });
        setExportProgress({ page: 0, totalPages: 0 });
        setExportOpen(true);
    };

    const closeExportDialog = () => {
        if (exporting) return;
        setExportOpen(false);
    };

    const cancelExport = () => {
        exportAbortRef.current?.abort();
        setExporting(false);
        setExportProgress({ page: 0, totalPages: 0 });
        notifyRef.current("Export cancelled", "info");
    };

    const handleExportExcel = async () => {
        const controller = new AbortController();
        exportAbortRef.current = controller;
        setExporting(true);
        setExportProgress({ page: 1, totalPages: 0 });

        try {
            const allRows = [];
            let pageNo = 1;
            let lastPage = 1;

            do {
                if (controller.signal.aborted) return;
                const params = buildContactsQueryParams({
                    page: pageNo,
                    perPage: EXPORT_PAGE_SIZE,
                    ...exportFilters,
                });
                const res = await axiosInstance.get(`${base_url}/crm/contacts`, {
                    params,
                    signal: controller.signal,
                });
                const paginator = res.data?.data ?? {};
                const rows = Array.isArray(paginator.data) ? paginator.data : [];
                lastPage = Number(paginator.last_page) || Math.max(1, Math.ceil((paginator.total || rows.length) / EXPORT_PAGE_SIZE));
                allRows.push(...rows);
                setExportProgress({ page: pageNo, totalPages: lastPage });
                if (rows.length < EXPORT_PAGE_SIZE) break;
                pageNo += 1;
            } while (pageNo <= lastPage && pageNo <= 500);

            if (controller.signal.aborted) return;

            if (allRows.length === 0) {
                notifyRef.current("No contacts match these filters", "error");
                return;
            }

            downloadContactsExcel(allRows);
            notifyRef.current(`Exported ${allRows.length} contact${allRows.length !== 1 ? "s" : ""}`, "success");
            setExportOpen(false);
        } catch (err) {
            if (controller.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                return;
            }
            notifyRef.current(err?.response?.data?.message || "Failed to export contacts", "error");
        } finally {
            exportAbortRef.current = null;
            setExporting(false);
            setExportProgress({ page: 0, totalPages: 0 });
        }
    };

    const toggleBot = async (c) => {
        const cid        = c.id ?? c.contact_id;
        const newVal     = !Boolean(c.is_bot_enabled);
        setTogglingBot(cid);
        try {
            await axiosInstance.patch(`${base_url}/crm/contacts/${cid}/bot-enabled`, { is_bot_enabled: newVal });
            notifyRef.current(`Bot reply ${newVal ? "enabled" : "disabled"} for ${c.name || c.phone}`, "success");
            // Optimistic update in local state
            setContacts((prev) => prev.map((x) => (x.id ?? x.contact_id) === cid ? { ...x, is_bot_enabled: newVal } : x));
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to update bot setting", "error");
        } finally {
            setTogglingBot(null);
        }
    };

    const totalPages = Math.ceil(total / perPage);

    return (
        <Box sx={WB_SX.page}>
            {/* Page header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>CRM Contacts</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} contact{total !== 1 ? "s" : ""} found
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    {userIsCrmAdmin && (
                    <Button
                        variant="outlined"
                        startIcon={<LocalOfferOutlined fontSize="small" />}
                        size="small"
                        onClick={() => navigate("/crm/tags")}
                        sx={{ ...WB_SX.secondaryButton, fontSize: "0.82rem" }}
                    >
                        Create Tags
                    </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlined fontSize="small" />}
                        size="small"
                        onClick={openExportDialog}
                        sx={{ ...WB_SX.secondaryButton, fontSize: "0.82rem" }}
                    >
                        Export Excel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutline fontSize="small" />}
                        onClick={() => setAddDialogOpen(true)}
                        sx={WB_SX.primaryButton}
                    >
                        Add Contact
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchContacts(page, search, status, source, tagFilter)} sx={WB_SX.secondaryButton}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
                <TextField
                    size="small"
                    placeholder="Search name, phone, company..."
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
                />
                {/* Status toggle buttons */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, border: "1px solid #e5e7eb", borderRadius: 2, p: 0.5, backgroundColor: "#f9fafb" }}>
                    {[
                        { value: "",               label: "All"          },
                        { value: "interested",     label: "Interested"   },
                        { value: "not_interested", label: "Not Interested" },
                    ].map(({ value: v, label }) => {
                        const active = status === v;
                        const activeColor = v === "interested" ? { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" }
                                          : v === "not_interested" ? { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" }
                                          : { bg: "#ffffff", text: "#111827", border: "#e5e7eb" };
                        return (
                            <Button
                                key={v}
                                size="small"
                                onClick={() => { setStatus(v); setPage(1); }}
                                sx={{
                                    px: 1.5, py: 0.4,
                                    fontSize: "0.75rem",
                                    fontWeight: active ? 700 : 500,
                                    borderRadius: 1.5,
                                    textTransform: "none",
                                    minWidth: "unset",
                                    backgroundColor: active ? activeColor.bg : "transparent",
                                    color: active ? activeColor.text : "text.secondary",
                                    border: active ? `1px solid ${activeColor.border}` : "1px solid transparent",
                                    "&:hover": { backgroundColor: active ? activeColor.bg : "rgba(0,0,0,0.04)" },
                                    transition: "all 0.15s ease",
                                }}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Source</InputLabel>
                    <Select value={source} label="Source" onChange={handleSourceChange} sx={{ borderRadius: 2 }}>
                        {SOURCE_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s || "All sources"}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {availableTags.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Tag</InputLabel>
                        <Select value={tagFilter} label="Tag" onChange={handleTagChange} sx={{ borderRadius: 2 }}>
                            <MenuItem value="">All tags</MenuItem>
                            {availableTags.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>{t.tag_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* Tag chips (quick filter) */}
            {availableTags.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        mb: 2.5,
                        pb: 0.5,
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": { height: 6 },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "#d1d5db", borderRadius: 3 },
                    }}
                >
                    {availableTags.map((t, ti) => {
                        const c = TAG_COLOR_PALETTE[ti % TAG_COLOR_PALETTE.length];
                        const tid = String(t.id);
                        const active = tagFilter === tid;
                        return (
                            <Chip
                                key={t.id}
                                label={t.tag_name}
                                size="small"
                                onClick={() => { setTagFilter(active ? "" : tid); setPage(1); }}
                                sx={{
                                    flexShrink: 0,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    backgroundColor: active ? c.text : c.bg,
                                    color: active ? "#fff" : c.text,
                                    border: `1px solid ${c.text}22`,
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                    "&:hover": { backgroundColor: c.text, color: "#fff" },
                                }}
                            />
                        );
                    })}
                    {tagFilter && (
                        <Chip
                            label="Clear tag filter"
                            size="small"
                            onDelete={() => { setTagFilter(""); setPage(1); }}
                            sx={{ flexShrink: 0, fontSize: "0.72rem", color: "text.secondary" }}
                        />
                    )}
                </Box>
            )}

            {/* Table */}
            <Card elevation={0} sx={WB_SX.card}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((h) => (
                                    <TableCell key={h} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : contacts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5 }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">No contacts found.</Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddCircleOutline fontSize="small" />}
                                                onClick={() => setAddDialogOpen(true)}
                                                sx={{ ...WB_SX.secondaryButton, mt: 0.5 }}
                                            >
                                                Add your first contact
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                contacts.map((c, i) => {
                                    const cid = c.id ?? c.contact_id;
                                    return (
                                        <TableRow key={cid ?? i} sx={{ ...WB_SX.tableRow, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                            {/* Contact */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                                                    <CRMAvatar initials={getInitials(c.name)} size={30} color="#1a2333" />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            onClick={() => cid && window.open(`/crm/contacts/${cid}`, "_blank", "noopener,noreferrer")}
                                                            sx={{
                                                                fontWeight: 600,
                                                                cursor: cid ? "pointer" : "default",
                                                                "&:hover": cid ? { color: "#1d4ed8", textDecoration: "underline" } : {},
                                                            }}
                                                        >
                                                            {c.name || "—"}
                                                        </Typography>
                                                        {c.email && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{c.email}</Typography>
                                                        )}
                                                        {Array.isArray(c.tags) && c.tags.length > 0 && (
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mr: 0.25 }}>
                                                                    Tags:
                                                                </Typography>
                                                                {c.tags.map((t, ti) => {
                                                                    const tc = TAG_COLOR_PALETTE[ti % TAG_COLOR_PALETTE.length];
                                                                    return (
                                                                        <CRMBadge key={t.id ?? ti} label={t.tag_name} bg={tc.bg} text={tc.text} />
                                                                    );
                                                                })}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{c.phone || "—"}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{c.alternate_contact_number || "—"}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.company_name || "—"}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                {c.source ? <CRMBadge label={c.source} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                                            </TableCell>

                                            {/* Bot Reply — WB-AGENT */}
                                            {showCrmBotControls && (
                                            <TableCell>
                                                <Tooltip title={Boolean(c.is_bot_enabled) ? "Bot enabled — click to disable" : "Bot disabled — click to enable"}>
                                                    <span>
                                                        <Switch
                                                            size="small"
                                                            checked={Boolean(c.is_bot_enabled)}
                                                            disabled={togglingBot === cid}
                                                            onChange={() => toggleBot(c)}
                                                            sx={{
                                                                "& .MuiSwitch-switchBase.Mui-checked": { color: BOT.switchOn },
                                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BOT.switchOn },
                                                            }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            )}

                                            <TableCell>
                                                <Box sx={{ display: "flex", gap: 0.75 }}>
                                                    <Tooltip title="Open contact">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!cid}
                                                                onClick={() => window.open(`/crm/contacts/${cid}`, "_blank", "noopener,noreferrer")}
                                                                sx={{ color: "text.secondary", "&:hover": { color: "#1d4ed8" } }}
                                                            >
                                                                <OpenInNew sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Edit Contact">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setEditTarget(c)}
                                                            sx={{ color: "text.secondary", "&:hover": { color: "#3b82f6" } }}
                                                        >
                                                            <EditOutlined sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={c.phone ? "Open Chat" : "No phone number"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!c.phone}
                                                                onClick={() => window.open(`/whatsapp-business/chat-window/contact/${c.phone}`, "_blank", "noopener,noreferrer")}
                                                                sx={{ color: "text.secondary", "&:hover": { color: "#25d366" }, "&.Mui-disabled": { opacity: 0.35 } }}
                                                            >
                                                                <ChatOutlined sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Contact">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setDeleteTarget(c)}
                                                            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                                                        >
                                                            <DeleteOutline sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, v) => setPage(v)}
                            size="small"
                            shape="rounded"
                        />
                    </Box>
                )}
            </Card>

            <ExportContactsDialog
                open={exportOpen}
                filters={exportFilters}
                onFiltersChange={setExportFilters}
                availableTags={availableTags}
                exporting={exporting}
                progress={exportProgress}
                onClose={closeExportDialog}
                onExport={handleExportExcel}
                onCancelExport={cancelExport}
            />

            {/* Add Contact Dialog */}
            <AddContactDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onCreated={handleContactCreated}
                showCrmBotControls={showCrmBotControls}
                availableTags={availableTags}
            />

            {/* Edit Contact Dialog */}
            <EditContactDialog
                open={!!editTarget}
                contact={editTarget}
                onClose={() => setEditTarget(null)}
                onUpdated={() => fetchContacts(page, search, status, source, tagFilter)}
                showCrmBotControls={showCrmBotControls}
                availableTags={availableTags}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={!!deleteTarget}
                contact={deleteTarget}
                onClose={() => !deleting && setDeleteTarget(null)}
                onConfirm={confirmDelete}
                deleting={deleting}
            />
        </Box>
    );
};

export default CRMContacts;
